import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  serverTimestamp,
  doc,
  updateDoc,
  onSnapshot,
  increment,
  setDoc,
} from "firebase/firestore";
import { db, storage } from "../../../../backend/firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const ANNOUNCEMENTS_COLLECTION = collection(
  db,
  "Marketing",
  "Announcements",
  "Items",
);

const isNewFile = (file) => {
  return file instanceof File;
};

const uploadAnnouncementFile = async ({ file, announcementId, folder }) => {
  if (!file) return null;

  // Existing uploaded file from Firestore
  if (file.url && !isNewFile(file)) {
    return file;
  }

  // New browser File
  const safeName = `${file.name}`;
  const fileRef = ref(
    storage,
    `Marketing/Announcements/${announcementId}/${folder}/${safeName}`,
  );

  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);

  return {
    name: file.name || "",
    type: file.type || "",
    size: file.size || 0,
    url,
    storagePath: fileRef.fullPath,
  };
};

const uploadAnnouncementFiles = async ({
  files = [],
  announcementId,
  folder,
}) => {
  const normalisedFiles = Array.isArray(files) ? files : [files];

  const uploaded = await Promise.all(
    normalisedFiles.map((file) =>
      uploadAnnouncementFile({
        file,
        announcementId,
        folder,
      }),
    ),
  );

  return uploaded.filter(Boolean);
};

const getAnnouncementLiveDate = (formData, status) => {
  const now = new Date();

  if (status === "draft" || status === "archived") {
    return {
      postedAt: null,
      postedAtMs: null,
      postedDate: "",
      postedTime: "",
    };
  }

  if (status === "scheduled") {
    return {
      postedAt: null,
      postedAtMs: null,
      postedDate: "",
      postedTime: "",
    };
  }

  return {
    postedAt: serverTimestamp(),
    postedAtMs: now.getTime(),
    postedDate: now.toISOString().slice(0, 10),
    postedTime: now.toTimeString().slice(0, 5),
  };
};

const getUpdatedLiveDateFields = (formData, nextStatus) => {
  const existingPostedAtMs = formData.postedAtMs || null;

  if (nextStatus === "active" && !existingPostedAtMs) {
    const now = new Date();

    return {
      postedAt: serverTimestamp(),
      postedAtMs: now.getTime(),
      postedDate: now.toISOString().slice(0, 10),
      postedTime: now.toTimeString().slice(0, 5),
    };
  }

  if (nextStatus === "draft" || nextStatus === "archived") {
    return {
      postedAt: formData.postedAt || null,
      postedAtMs: formData.postedAtMs || null,
      postedDate: formData.postedDate || "",
      postedTime: formData.postedTime || "",
    };
  }

  return {
    postedAt: formData.postedAt || null,
    postedAtMs: formData.postedAtMs || null,
    postedDate: formData.postedDate || "",
    postedTime: formData.postedTime || "",
  };
};

export const createAnnouncement = async ({
  formData,
  status = "active",
  createdBy = "",
}) => {
  const now = new Date();
  const liveDateFields = getAnnouncementLiveDate(formData, status);

  const basePayload = {
    announcementFor: formData.announcementFor || [],

    scheduleAnnouncement: !!formData.scheduleAnnouncement,
    scheduleDate: formData.scheduleDate || "",
    scheduleTime: formData.scheduleTime || "",

    keepUntilDate: formData.keepUntilDate || "",
    keepUntilTime: formData.keepUntilTime || "",

    notifyOnLogin: !!formData.notifyOnLogin,

    contentType: formData.contentType || "",
    contentTitle: formData.contentTitle || "",
    content: formData.content || "",

    acknowledgementType: formData.acknowledgementType || "acknowledged",
    acknowledgementText: formData.acknowledgementText || "",

    url: formData.url || "",

    status,
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdAtMs: now.getTime(),
    clickCount: 0,
    acknowledgementCount: 0,
    postedAt: liveDateFields.postedAt,
    postedAtMs: liveDateFields.postedAtMs,
    postedDate: liveDateFields.postedDate,
    postedTime: liveDateFields.postedTime,

    headerImageFiles: [],
    documentFiles: [],
  };

  const docRef = await addDoc(ANNOUNCEMENTS_COLLECTION, basePayload);

  const headerImageFiles = await uploadAnnouncementFiles({
    files: formData.headerImageFiles || [],
    announcementId: docRef.id,
    folder: "HeaderImages",
  });

  const documentFiles = await uploadAnnouncementFiles({
    files: formData.documentFiles || [],
    announcementId: docRef.id,
    folder: "Documents",
  });

  await updateDoc(docRef, {
    headerImageFiles,
    documentFiles,
    updatedAt: serverTimestamp(),
  });

  return {
    id: docRef.id,
    ...basePayload,
    headerImageFiles,
    documentFiles,
  };
};

export const getAnnouncements = async () => {
  const q = query(ANNOUNCEMENTS_COLLECTION, orderBy("createdAtMs", "desc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
};

export const subscribeAnnouncements = (callback, onError) => {
  const q = query(ANNOUNCEMENTS_COLLECTION, orderBy("createdAtMs", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const announcements = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      // console.log("[Announcements DB] Live announcements received:", {
      //   count: announcements.length,
      //   announcements,
      // });

      callback(announcements);
    },
    (error) => {
      // console.error("[Announcements DB] Subscribe error:", error);
      onError?.(error);
    },
  );
};
export const getAnnouncementById = async (announcementId) => {
  const announcementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "Items",
    announcementId,
  );

  const snap = await getDoc(announcementRef);

  if (!snap.exists()) {
    // console.log("[Announcement DB] No announcement found:", announcementId);
    return null;
  }

  const data = {
    id: snap.id,
    ...snap.data(),
  };

  // console.log("[Announcement DB] Single announcement received:", {
  //   announcementId,
  //   data,
  //   acknowledgementDetails: data.acknowledgementDetails,
  //   clickCount: data.clickCount,
  //   acknowledgementCount: data.acknowledgementCount,
  //   resourceClickCount: data.resourceClickCount,
  //   uniqueResourceClickUserCount: data.uniqueResourceClickUserCount,
  // });

  return data;
};

export const updateAnnouncement = async (announcementId, formData) => {
  const announcementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "Items",
    announcementId,
  );

  const headerImageFiles = await uploadAnnouncementFiles({
    files: formData.headerImageFiles || [],
    announcementId,
    folder: "HeaderImages",
  });

  const documentFiles = await uploadAnnouncementFiles({
    files: formData.documentFiles || [],
    announcementId,
    folder: "Documents",
  });

  const nextStatus =
    formData.status || (formData.scheduleAnnouncement ? "scheduled" : "active");

  const liveDateFields = getUpdatedLiveDateFields(formData, nextStatus);

  const payload = {
    announcementFor: formData.announcementFor || [],

    scheduleAnnouncement: !!formData.scheduleAnnouncement,
    scheduleDate: formData.scheduleDate || "",
    scheduleTime: formData.scheduleTime || "",

    keepUntilDate: formData.keepUntilDate || "",
    keepUntilTime: formData.keepUntilTime || "",

    notifyOnLogin: !!formData.notifyOnLogin,

    contentType: formData.contentType || "",
    contentTitle: formData.contentTitle || "",
    content: formData.content || "",

    acknowledgementType: formData.acknowledgementType || "acknowledged",
    acknowledgementText: formData.acknowledgementText || "",

    url: formData.url || "",

    status: nextStatus,

    postedAt: liveDateFields.postedAt,
    postedAtMs: liveDateFields.postedAtMs,
    postedDate: liveDateFields.postedDate,
    postedTime: liveDateFields.postedTime,

    updatedAt: serverTimestamp(),

    headerImageFiles,
    documentFiles,
  };

  await updateDoc(announcementRef, payload);

  return {
    id: announcementId,
    ...payload,
  };
};

export const updateAnnouncementStatus = async (announcementId, status) => {
  const announcementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "Items",
    announcementId,
  );

  const snap = await getDoc(announcementRef);
  const current = snap.exists() ? snap.data() : {};

  const now = new Date();

  const statusPayload = {
    status,
    updatedAt: serverTimestamp(),
  };

  if (status === "active" && !current.postedAtMs) {
    statusPayload.postedAt = serverTimestamp();
    statusPayload.postedAtMs = now.getTime();
    statusPayload.postedDate = now.toISOString().slice(0, 10);
    statusPayload.postedTime = now.toTimeString().slice(0, 5);
  }

  await updateDoc(announcementRef, statusPayload);
};

const getScheduledDateTime = (announcement) => {
  if (!announcement.scheduleDate) return null;

  const safeTime = announcement.scheduleTime || "00:00";
  const dateTime = new Date(`${announcement.scheduleDate}T${safeTime}`);

  if (Number.isNaN(dateTime.getTime())) return null;

  return dateTime;
};

const buildPostedFieldsFromScheduledDate = (announcement) => {
  const scheduledAt = getScheduledDateTime(announcement);

  if (!scheduledAt) {
    const now = new Date();

    return {
      postedAt: serverTimestamp(),
      postedAtMs: now.getTime(),
      postedDate: now.toISOString().slice(0, 10),
      postedTime: now.toTimeString().slice(0, 5),
    };
  }

  return {
    postedAtMs: scheduledAt.getTime(),
    postedDate: announcement.scheduleDate || "",
    postedTime: announcement.scheduleTime || "",
  };
};

export const syncLiveAnnouncements = async (announcements = []) => {
  const now = new Date();

  const updates = announcements.map(async (announcement) => {
    if (announcement.status === "archived" || announcement.status === "draft") {
      return announcement;
    }

    if (!announcement.scheduleAnnouncement) {
      if (announcement.status === "active" && !announcement.postedAtMs) {
        await updateAnnouncementStatus(announcement.id, "active");

        return {
          ...announcement,
          status: "active",
          postedAtMs: now.getTime(),
          postedDate: now.toISOString().slice(0, 10),
          postedTime: now.toTimeString().slice(0, 5),
        };
      }

      return announcement;
    }

    const scheduledAt = getScheduledDateTime(announcement);

    if (!scheduledAt || scheduledAt > now) {
      return announcement;
    }

    if (announcement.status === "active" && announcement.postedAtMs) {
      return announcement;
    }

    const postedFields = buildPostedFieldsFromScheduledDate(announcement);

    const announcementRef = doc(
      db,
      "Marketing",
      "Announcements",
      "Items",
      announcement.id,
    );

    await updateDoc(announcementRef, {
      status: "active",
      ...postedFields,
      updatedAt: serverTimestamp(),
    });

    return {
      ...announcement,
      status: "active",
      ...postedFields,
    };
  });

  return Promise.all(updates);
};

export const subscribeAnnouncementById = (
  announcementId,
  callback,
  onError,
) => {
  if (!announcementId) {
    callback?.(null);
    return () => {};
  }

  const announcementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "Items",
    announcementId,
  );

  return onSnapshot(
    announcementRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback?.(null);
        return;
      }

      const data = {
        id: snapshot.id,
        ...snapshot.data(),
      };

      // console.log("[Announcement Stats DB] Live announcement:", data);

      callback?.(data);
    },
    (error) => {
      // console.error("[Announcement Stats DB] Listener error:", error);
      onError?.(error);
    },
  );
};